import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const themes = {
  dark: {
    navy: '#0f172a',
    lightNavy: '#1e293b',
    lighterNavy: '#334155',
    slate: '#64748b',
    lightSlate: '#94a3b8',
    lighterSlate: '#cbd5e1',
    accent: '#3b82f6',
    secondary: '#8b5cf6',
    tertiary: '#06b6d4',
    background: '#0f172a',
    text: '#cbd5e1',
    textMuted: '#64748b'
  },
  light: {
    navy: '#ffffff',
    lightNavy: '#f8fafc',
    lighterNavy: '#f1f5f9',
    slate: '#475569',
    lightSlate: '#334155',
    lighterSlate: '#1e293b',
    accent: '#2563eb',
    secondary: '#7c3aed',
    tertiary: '#0891b2',
    background: '#ffffff',
    text: '#1e293b',
    textMuted: '#475569'
  }
};

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('dark');

  useEffect(() => {
    // Check for saved theme preference or default to 'dark'
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setCurrentTheme(savedTheme);
    
    // Apply theme to document root
    const root = document.documentElement;
    const theme = themes[savedTheme];
    
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
  }, []);

  const toggleTheme = () => {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    setCurrentTheme(newTheme);
    localStorage.setItem('theme', newTheme);
    
    // Apply new theme to document root
    const root = document.documentElement;
    const theme = themes[newTheme];
    
    Object.entries(theme).forEach(([key, value]) => {
      root.style.setProperty(`--color-${key}`, value);
    });
  };

  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
      localStorage.setItem('theme', themeName);
      
      const root = document.documentElement;
      const theme = themes[themeName];
      
      Object.entries(theme).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
    }
  };

  const value = {
    currentTheme,
    themes,
    theme: themes[currentTheme],
    toggleTheme,
    setTheme,
    isDark: currentTheme === 'dark',
    isLight: currentTheme === 'light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};